import { BaseApiService } from '../base/BaseApiService';
import {
  CreatePlanRequest,
  UpdatePlanRequest,
  PlanResponse,
  CreatePlanServiceRequest,
  UpdatePlanServiceRequest,
  PlanServiceResponse,
  PlanFilters
} from '../../types/dtos';
import { ApiResponse, PaginationParams, PaginatedResponse } from '../../types/api';

export class PlanServices extends BaseApiService {
  // ===== MÉTODOS DE PLANOS =====

  // Buscar todos os planos (sem paginação)
  async getAllPlans(): Promise<PlanResponse[]> {
    const response = await this.api.get<PlanResponse[]>('/plans');
    return response.data;
  }

  // Buscar planos com paginação
  async getPlans(params?: PaginationParams): Promise<PaginatedResponse<PlanResponse>> {
    const response = await this.api.get<ApiResponse<PaginatedResponse<PlanResponse>>>('/plans', { params });
    return this.extractData(response);
  }

  // Buscar plano por ID
  async getPlanById(id: string): Promise<PlanResponse> {
    const response = await this.api.get<PlanResponse>(`/plans/${id}`);
    return response.data;
  }

  // Buscar plano por nome
  async getPlanByName(name: string): Promise<PlanResponse> {
    const response = await this.api.get<PlanResponse>(`/plans/by-name/${encodeURIComponent(name)}`);
    return response.data;
  }

  // Buscar planos por faixa de preço
  async getPlansByPriceRange(minPrice: number, maxPrice: number): Promise<PlanResponse[]> {
    const response = await this.api.get<PlanResponse[]>(`/plans/by-price-range?minPrice=${minPrice}&maxPrice=${maxPrice}`);
    return response.data;
  }

  // Buscar planos por franquia de dados
  async getPlansByDataAllowance(minDataMb: number, maxDataMb: number): Promise<PlanResponse[]> {
    const response = await this.api.get<PlanResponse[]>(`/plans/by-data-allowance?minDataMb=${minDataMb}&maxDataMb=${maxDataMb}`);
    return response.data;
  }

  // Buscar planos com filtros avançados
  async getPlansWithFilters(filters: PlanFilters): Promise<PlanResponse[]> {
    const response = await this.api.get<PlanResponse[]>('/plans/filtered', { params: filters });
    return response.data;
  }

  // Criar plano (com serviços)
  async createPlan(planData: CreatePlanRequest): Promise<PlanResponse> {
    const response = await this.api.post<PlanResponse>('/plans', planData);
    return response.data;
  }

  // Atualizar plano
  async updatePlan(id: string, planData: UpdatePlanRequest): Promise<void> {
    await this.api.put(`/plans/${id}`, planData);
  }

  // Deletar plano
  async deletePlan(id: string): Promise<void> {
    await this.api.delete(`/plans/${id}`);
  }

  // ===== MÉTODOS DE SERVIÇOS DO PLANO =====

  // Buscar todos os serviços de um plano
  async getPlanServices(planId: string): Promise<PlanServiceResponse[]> {
    const response = await this.api.get<PlanServiceResponse[]>(`/plans/${planId}/services`);
    return response.data;
  }

  // Buscar serviço específico de um plano
  async getPlanServiceById(planId: string, serviceId: string): Promise<PlanServiceResponse> {
    const response = await this.api.get<PlanServiceResponse>(`/plans/${planId}/services/${serviceId}`);
    return response.data;
  }

  // Adicionar serviço a um plano
  async addServiceToPlan(planId: string, serviceData: CreatePlanServiceRequest): Promise<PlanServiceResponse> {
    const response = await this.api.post<PlanServiceResponse>(`/plans/${planId}/services`, serviceData);
    return response.data;
  }

  // Atualizar serviço de um plano
  async updatePlanService(planId: string, serviceId: string, serviceData: UpdatePlanServiceRequest): Promise<void> {
    await this.api.put(`/plans/${planId}/services/${serviceId}`, serviceData);
  }

  // Deletar serviço de um plano
  async deletePlanService(planId: string, serviceId: string): Promise<void> {
    await this.api.delete(`/plans/${planId}/services/${serviceId}`);
  }

  // ===== MÉTODOS ESTATÍSTICOS =====

  // Obter preço médio dos planos
  async getAveragePrice(): Promise<number> {
    const response = await this.api.get<{ averagePrice: number }>('/plans/statistics/average-price');
    return response.data.averagePrice;
  }

  // Obter plano mais barato
  async getCheapestPlan(): Promise<PlanResponse> {
    const response = await this.api.get<PlanResponse>('/plans/statistics/cheapest');
    return response.data;
  }

  // Obter plano mais caro
  async getMostExpensivePlan(): Promise<PlanResponse> {
    const response = await this.api.get<PlanResponse>('/plans/statistics/most-expensive');
    return response.data;
  }

  // ===== MÉTODOS UTILITÁRIOS =====

  // Verificar se um plano existe
  async planExists(id: string): Promise<boolean> {
    try {
      await this.getPlanById(id);
      return true;
    } catch {
      return false;
    }
  }

  // Verificar se um nome de plano já existe
  async planNameExists(name: string, excludeId?: string): Promise<boolean> {
    try {
      const plan = await this.getPlanByName(name);
      return excludeId ? plan.id !== excludeId : true;
    } catch {
      return false;
    }
  }

  // Obter planos para dropdown/select
  async getPlansForDropdown(): Promise<Array<{ id: string; name: string; basePrice: number }>> {
    const plans = await this.getAllPlans();
    return plans.map(plan => ({
      id: plan.id,
      name: plan.name,
      basePrice: plan.basePrice
    }));
  }

  // Obter serviços de um plano para dropdown/select
  async getPlanServicesForDropdown(planId: string): Promise<Array<{ id: string; name: string; monthlyValue: number }>> {
    const services = await this.getPlanServices(planId);
    return services.map(service => ({
      id: service.id,
      name: service.name,
      monthlyValue: service.monthlyValue
    }));
  }

  // Calcular preço total de um plano (base + serviços habilitados)
  calculateTotalPrice(plan: PlanResponse): number {
    const enabledServicesTotal = plan.services
      .filter(service => service.enabled)
      .reduce((total, service) => total + service.monthlyValue, 0);
    
    return plan.basePrice + enabledServicesTotal;
  }

  // Converter franquia de MB para GB
  convertMbToGb(mb: number): number {
    return Number((mb / 1024).toFixed(2));
  }

  // Converter franquia de GB para MB
  convertGbToMb(gb: number): number {
    return Math.round(gb * 1024);
  }

  // Validar dados de criação de plano
  validatePlanData(planData: CreatePlanRequest): string[] {
    const errors: string[] = [];

    if (!planData.name || planData.name.trim().length < 2) {
      errors.push('Nome do plano deve ter pelo menos 2 caracteres');
    }

    if (!planData.description || planData.description.trim().length < 10) {
      errors.push('Descrição deve ter pelo menos 10 caracteres');
    }

    if (planData.basePrice <= 0) {
      errors.push('Preço base deve ser maior que zero');
    }

    if (planData.dataAllowanceGb <= 0) {
      errors.push('Franquia de dados deve ser maior que zero');
    }

    // Validar serviços se fornecidos
    if (planData.services && planData.services.length > 0) {
      planData.services.forEach((service, index) => {
        if (!service.name || service.name.trim().length < 2) {
          errors.push(`Serviço ${index + 1}: Nome deve ter pelo menos 2 caracteres`);
        }
        if (service.monthlyValue <= 0) {
          errors.push(`Serviço ${index + 1}: Valor mensal deve ser maior que zero`);
        }
      });
    }

    return errors;
  }

  // Validar dados de serviço
  validateServiceData(serviceData: CreatePlanServiceRequest): string[] {
    const errors: string[] = [];

    if (!serviceData.name || serviceData.name.trim().length < 2) {
      errors.push('Nome do serviço deve ter pelo menos 2 caracteres');
    }

    if (serviceData.monthlyValue <= 0) {
      errors.push('Valor mensal deve ser maior que zero');
    }

    return errors;
  }
}

// Instância singleton
export const planServices = new PlanServices();
export default planServices;
